<script setup lang="ts" inheritAttrs="false">
import { useSmarticoConfigs } from '@/stores/smartico/configs';
import { useSmarticoMessages } from '@/stores/smartico/messages';
import { useWalletStore } from '@/stores/wallet';
import { copyToClipboard } from '@/utils/apostatudo/clipboard';

const { userName } = useUserData();
const { currentUserID } = storeToRefs(useWalletStore());

const smarticoUserInfosStore = useSmarticoUserInfos();

const {
  currentUserLevelName,
  currentUserLevelImage,
  currentUserLevelData,
  nextUserLevel,
  userInfosProfile,
  isLoadingUserInfos,
} = storeToRefs(smarticoUserInfosStore);

const { loadUserInfosProfile } = smarticoUserInfosStore;
const { isSuccessCheckUserIdentify, isReadyGamefication } =
  storeToRefs(useSmarticoInit());

// Este componente é usado dentro do dropdown do header. Removemos a
// configuração de SEO (título da página) — isso agora será tratado
// apenas por páginas dedicadas, caso existam.

const { logout: logoutAuth } = useAuthStore();

const route = useRoute();

function isPageActive(path: string) {
  return route.fullPath === path;
}

const userPointsInLevel = computed(() => {
  if (!userInfosProfile.value || !currentUserLevelData.value) return 0;

  return (
    userInfosProfile.value.ach_points_ever -
    currentUserLevelData.value.required_points
  );
});

const pointsToNextLevel = computed(() => {
  if (!nextUserLevel.value || !currentUserLevelData.value) return 0;

  return (
    nextUserLevel.value.required_points -
    currentUserLevelData.value.required_points
  );
});

const progressToAnotherLevel = computed(() => {
  if (
    !nextUserLevel.value ||
    !currentUserLevelData.value ||
    pointsToNextLevel.value === 0
  )
    return 0;

  return Math.min(
    (userPointsInLevel.value / pointsToNextLevel.value) * 100,
    100,
  );
});

const firstName = computed(() => userName.value.split(' ')[0]);

const { currentUserProfileAvatar } = storeToRefs(smarticoUserInfosStore);

const { isNotificationActived } = storeToRefs(useSmarticoConfigs());

// Emit event to allow parent to close the dropdown
const emit = defineEmits(['close']);
const { unReadMessages } = storeToRefs(useSmarticoMessages());

const unReadMessagesCount = computed(() => {
  return unReadMessages.value >= 9 ? '9+' : unReadMessages.value;
});

const showCopiedTooltip = ref(false);

const isUserInfosReady = computed(() => Boolean(userInfosProfile.value));

function formatCurrency(value?: number) {
  if (!isUserInfosReady.value) {
    return isLoadingUserInfos.value ? '...' : '-';
  }

  return value?.toLocaleString('pt-BR') ?? '0';
}

async function refreshUserInfosWhenReady() {
  if (!import.meta.client || !isSuccessCheckUserIdentify.value) {
    return;
  }

  await loadUserInfosProfile();
}

onMounted(() => {
  document.body.classList.add('overflow-hidden');
  refreshUserInfosWhenReady().catch(() => {});
});

onUnmounted(() => {
  document.body.classList.remove('overflow-hidden');
});

const formattedUserLevelName = computed(() => {
  if (!currentUserLevelName.value) return 'Nível Desconhecido';

  return currentUserLevelName.value.replace(
    /(\w+) LV (\d+)/,
    (match, p1, p2) =>
      `${p1.charAt(0).toUpperCase() + p1.slice(1).toLowerCase()} (Level ${p2})`,
  );
});

const currencies = computed(() => {
  return [
    {
      name: 'COINS',
      value: formatCurrency(userInfosProfile.value?.ach_points_balance),
      icon: 'currency-header/coin',
    },
    {
      name: 'DIAMONDS',
      value: formatCurrency(userInfosProfile.value?.ach_diamonds_balance),
      icon: 'currency-header/diamond',
    },
    {
      name: 'GEMS',
      value: formatCurrency(userInfosProfile.value?.ach_gems_balance),
      icon: 'currency-header/gem',
    },
  ];
});

watch(
  [isSuccessCheckUserIdentify, isReadyGamefication],
  ([isIdentified, isGameficationReady]) => {
    if (isIdentified || isGameficationReady) {
      refreshUserInfosWhenReady().catch(() => {});
    }
  },
  { immediate: true },
);

const userMenuIcons = [
  {
    id: 'carteira',
    label: 'CARTEIRA',
    url: '/user/wallet',
    imageUrl: '/assets/images/user/perfil/wallet.png',
    scale: 1.2,
  },
  {
    id: 'baixar-app',
    label: 'BAIXAR APP',
    url: '/promotions/aplicativo',
    imageUrl: '/assets/images/user/perfil/baixar-app.png',
    scale: 1.6,
  },
  {
    id: 'notificacoes',
    label: 'NOTIFICAÇÕES',
    url: '#',
    imageUrl: '/assets/images/user/perfil/notificacoes.png',
    scale: 1.15,
  },
  {
    id: 'historico',
    label: 'HISTÓRICO',
    url: '/user/history/casino',
    imageUrl: '/assets/images/user/perfil/historico.png',
    scale: 1.1,
  },
  {
    id: 'recompensas',
    label: 'RECOMPENSAS',
    url: '/user/rewards',
    imageUrl: '/assets/images/user/perfil/recompensas.png',
    scale: 1.2,
  },
  {
    id: 'indique',
    label: 'INDIQUE',
    url: '/user/refers',
    imageUrl: '/assets/images/user/perfil/indique.png',
    scale: 1.5,
  },
];

/*
  Use the `scale` property to increase or decrease the icon's size relative
  to the base. Examples:
  - scale: 1 -> default
  - scale: 1.5 -> 50% bigger
  - scale: 2 -> twice the size

  Notes:
  - The wrapper `.icon-scale-wrapper` preserves layout and allows the icon
    to overflow so the grid doesn't resize.
  - For icons that show a badge (e.g., notifications) the badge position
    is also adjusted with `getBadgePosition()` so it still appears at the
    top-right after scaling.
*/

function handleAvatarClick() {
  // Função para alterar a imagem do avatar usando a funcionalidade correta da Smartico
  useSmarticoUserInfos().openChangeAvatar();
}

async function handleLogout() {
  await logoutAuth({
    displayMessage: true,
    redirectToHome: true,
    origin: 'user',
  });
}

function handleSmarticoInboxClick() {
  if (typeof window !== 'undefined' && (window as any)._smartico) {
    (window as any)._smartico.dp('dp:inbox');
  } else {
    console.error('Smartico script not loaded or _smartico object not found.');
  }
}

function handleIconClick(icon: any) {
  if (icon.url === '#') {
    if (icon.id === 'notificacoes') {
      handleSmarticoInboxClick();

      // fecha o dropdown quando o usuário abre a caixa de mensagens
      emit('close');

      return;
    }

    return;
  }

  if (icon.id === 'baixar-app') {
    // Detecta o sistema operacional
    const ua =
      navigator.userAgent || navigator.vendor || (globalThis as any).opera;

    let scrollToText: string | null = null;

    if (/android/i.test(ua)) {
      scrollToText = 'Como baixar o App Android';
    } else if (/iPad|iPhone|iPod/.test(ua) || /ios/i.test(ua)) {
      scrollToText = 'Como baixar o App IOS';
    }

    // Navega para a página
    // fecha o dropdown antes da navegação
    emit('close');

    navigateTo(icon.url).then(() => {
      if (!scrollToText) return;
      setTimeout(() => {
        const headings = document.querySelectorAll(
          'h3.promo-torneios-heading-3',
        );

        for (const h of headings) {
          if (h.textContent && h.textContent.includes(scrollToText)) {
            h.scrollIntoView({ behavior: 'smooth', block: 'center' });
            break;
          }
        }
      }, 800);
    });

    return;
  }

  // fecha o dropdown antes da navegação
  emit('close');

  navigateTo(icon.url);
}

function handleEditProfile() {
  emit('close');
  navigateTo('/user/account');
}

function handleVerNiveis() {
  emit('close');
  navigateTo('/vip/levels');
}

function getIconStyle(icon: any) {
  // Use a default scale of 1 when not provided
  const scale = icon?.scale ?? 1;

  return {
    transform: `scale(${scale})`,
    transformOrigin: 'center',
  } as Record<string, string | number>;
}

function getBadgePosition(icon: any, anchor: 'icon' | 'card' = 'icon') {
  // Base offsets used to position the badge (tailwind -2 = -0.5rem = -8px)
  const baseOffset = 8;
  const scale = icon?.scale ?? 1;

  const offset = anchor === 'icon' ? -baseOffset * scale : 8; // px

  return {
    right: `${offset}px`,
    top: `${offset}px`,
  } as Record<string, string>;
}

async function handleCopyId() {
  if (currentUserID.value) {
    const ok = await copyToClipboard(currentUserID.value.toString());

    if (ok) {
      showCopiedTooltip.value = true;
      setTimeout(() => {
        showCopiedTooltip.value = false;
      }, 2000);
    }
  }
}
</script>

<template>
  <div
    class="relative flex h-[150px] max-w-[352px] flex-col justify-end items-center self-center mx-auto rounded-[9px] bg-white select-none"
    v-bind="$attrs"
  >
    <img
      alt="Avatar"
      class="absolute inset-0 h-full mx-auto object-cover rounded-[9px] cursor-pointer"
      :draggable="false"
      :src="currentUserProfileAvatar"
      @click="handleAvatarClick"
    />
    <NuxtIcon
      class="absolute top-1 right-1 text-[#000000]/50 text-[14px] cursor-pointer"
      name="user/edit"
      @click="handleAvatarClick"
    />
    <div
      class="absolute bottom-0 left-0 right-0 flex justify-between items-center self-stretch [background:rgba(0,7,23,0.8)] backdrop-blur-[2px] p-2 rounded-b-none z-20"
    >
      <div class="flex flex-col gap-[3px]">
        <span class="text-xs font-medium text-white">
          Olá {{ firstName || 'Usuário' }}!
        </span>
        <span
          v-if="currentUserID"
          class="text-[#00C9FF] text-[9px] font-medium leading-3 flex items-center gap-1 relative"
        >
          ID: #{{ currentUserID }}
          <NuxtIcon
            class="cursor-pointer text-[10px]"
            name="copy"
            @click="handleCopyId"
          />
          <div
            v-if="showCopiedTooltip"
            class="absolute right-[-45px] top-0 bg-[#00C9FF] text-white text-[8px] px-1 py-0.5 rounded whitespace-nowrap z-10"
          >
            Copiado
          </div>
        </span>
      </div>
      <button
        class="flex items-center justify-center gap-[3px] border border-[color:var(--A-BLUE2,#00C9FF)] [background:rgba(0,201,255,0.10)] px-[8px] py-1 rounded-md border-solid text-[9px] font-medium leading-3 text-white cursor-pointer hover:[background:rgba(0,201,255,0.25)] hover:border-[color:var(--A-BLUE2,#00C9FF)] hover:shadow-md"
        @click="handleEditProfile"
      >
        <span>EDITAR PERFIL</span>
        <NuxtIcon
          class="text-[10px]"
          :filled="true"
          name="user/edit"
        />
      </button>
    </div>
  </div>

  <div
    class="relative flex max-w-[352px] mt-[10px] lg:mt-[15px] gap-2 justify-start items-center mx-auto [background:var(--ATNB---BLUE-MAIN,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#1515E5_0%,#000098_100%))] backdrop-blur-[10px] p-3 pl-2 rounded-[10px] select-none"
  >
    <div class="text-center">
      <img
        alt="Level Icon"
        class="h-[50px] rounded-full object-cover -my-1"
        :draggable="false"
        :src="currentUserLevelImage"
      />
    </div>

    <div class="flex flex-col items-center gap-1.5 w-full">
      <div class="flex items-center justify-between w-full">
        <span class="text-white text-[12px] font-normal leading-[13px]">
          Você é
          <b>
            {{ formattedUserLevelName }}
          </b>
        </span>
        <button
          class="text-[#00C9FF] text-[11px] font-medium leading-[12px]"
          @click="handleVerNiveis"
        >
          Ver níveis
        </button>
      </div>
      <div class="w-full h-[10px] rounded-full overflow-hidden">
        <div class="w-full h-full bg-texts/20">
          <div
            class="h-full rounded-l-full bg-[#00C9FF] rounded-[9px]"
            :style="{ width: `${progressToAnotherLevel || 0}%` }"
          ></div>
        </div>
      </div>
    </div>
  </div>

  <span
    class="mx-auto w-full flex max-w-[352px] mt-[10px] lg:mt-[15px] text-white text-[15px] font-medium leading-[100%]"
    >Moedas</span
  >
  <div
    class="relative w-full flex max-w-[352px] mt-[10px] lg:mt-[15px] justify-between items-center mx-auto bg-white backdrop-blur-[10px] p-[15px] rounded-[10px] select-none"
  >
    <div
      v-for="currency in currencies"
      :key="currency.name"
      class="flex items-center gap-2"
    >
      <NuxtIcon
        class="text-[24px] text-black flex-shrink-0"
        filled
        :name="currency.icon"
      />
      <div class="flex flex-col items-start">
        <span
          class="text-[#000717] text-center text-xs font-bold leading-[15px]"
          >{{ currency.value }}</span
        >
        <span
          class="text-[#1515E5] text-center text-[10px] font-medium leading-[10px]"
          >{{ currency.name }}</span
        >
      </div>
    </div>
  </div>

  <span
    class="mx-auto w-full flex max-w-[352px] mt-[10px] lg:mt-[15px] text-white text-[15px] font-medium leading-[100%]"
    >Navegação</span
  >
  <div class="grid grid-cols-3 gap-2 mt-3 max-w-[352px] mx-auto">
    <div
      v-for="icon in userMenuIcons"
      :key="icon.id"
      :class="[
        'flex h-[70px] flex-col justify-between items-center [background:rgba(255,255,255,0.05)] backdrop-blur-[10px] px-2 py-[7px] rounded-[10px] cursor-pointer transition-colors',
        {
          active: isPageActive(icon.url),
          relative: icon.id === 'notificacoes',
        },
      ]"
      @click="handleIconClick(icon)"
    >
      <div class="relative flex-grow flex items-center justify-center">
        <div
          class="icon-scale-wrapper flex items-center justify-center"
          :style="getIconStyle(icon)"
        >
          <NuxtImg
            class="w-auto h-auto max-w-full max-h-full object-contain"
            :src="getAssetsUrlForCDN(icon.imageUrl)"
          />
        </div>

        <!-- (badge movido para fora do wrapper do ícone: será posicionado no cartão) -->
      </div>

      <!-- Badge no canto superior direito do card (externo ao ícone) -->
      <div
        v-if="
          icon.id === 'notificacoes' &&
          isNotificationActived &&
          unReadMessages >= 0
        "
        class="w-3 h-3 absolute bg-[radial-gradient(ellipse_138.00%_168.30%_at_36.76%_-16.67%,_#FF704A_0%,_#FF3400_100%)] rounded-lg flex justify-center items-center gap-0.5 z-30"
        :style="getBadgePosition(icon, 'card')"
      >
        <div
          class="text-center justify-center text-white text-[7px] font-bold leading-[8px]"
        >
          {{ unReadMessagesCount }}
        </div>
      </div>
      <span class="text-white text-[9px] font-medium leading-2">
        {{ icon.label }}
      </span>
    </div>
  </div>

  <button
    class="menu-button relative w-full flex max-w-[352px] mt-[10px] lg:mt-[15px] h-[42px] justify-center items-center gap-2.5 mx-auto [background:rgba(255,255,255,0.05)] backdrop-blur-[10px] px-4 py-3 rounded-[10px] select-none"
    @click="handleLogout"
  >
    <NuxtIcon
      class="text-white text-[13px]"
      name="user/sair-conta"
    />
    <span class="text-white text-[11px] font-semibold leading-4">
      SAIR DA CONTA
    </span>
  </button>
</template>

<style scoped>
.fade-in {
  opacity: 0;
  transition: opacity 0.3s ease;
}

.fade-in.loaded {
  opacity: 1;
}

/* Prevent tap highlight on iOS for interactive menu cards and logout button */
.grid > div,
.menu-button {
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
}

.grid > div:hover,
.menu-button:hover {
  background-color: rgba(71, 85, 105, 0.7) !important;
}

.grid > div.active {
  background: rgba(8, 86, 150, 0.719) !important;
}

.icon-scale-wrapper {
  width: 36px; /* base icon area */
  height: 36px; /* base icon area */
  display: flex; /* Changed to flex */
  align-items: center;
  justify-content: center;
  overflow: visible; /* allow the icon to grow outside the area */

  /* Keep any nested image from stretching vertically on Safari / iOS.
     Force icons to respect the wrapper's aspect ratio. */
}

.icon-scale-wrapper img,
.icon-scale-wrapper picture,
.icon-scale-wrapper svg {
  width: auto; /* don't force the image to fill the width */
  height: auto; /* keep aspect ratio */
  max-width: 100%;
  max-height: 100%;
  object-fit: contain; /* preserve natural proportions */
  display: block; /* avoid inline whitespace baseline issues */
}

/* Remove blue tap/highlight on iOS when tapping elements and avoid focus ring
   around the dropdown icons/cards. Keep it scoped to relevant elements for
   accessibility reasons. */

/* Remove blue tap/highlight on iOS when tapping elements and avoid focus ring
   around the dropdown icons/cards. Keep it scoped to relevant elements for
   accessibility reasons. */
.icon-scale-wrapper,
.icon-scale-wrapper img,
.icon-scale-wrapper svg {
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
}

.grid > div:focus:not(:focus-visible),
.menu-button:focus:not(:focus-visible) {
  /* Remove the browser default focus styles on touch; keep keyboard focus
     visible for accessibility via :focus-visible */
  outline: none;
  box-shadow: none;
}
</style>
